import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getArchivedListNextPage } from '../../actions/archivedList';
import { getChatListNextPage } from '../../actions/chatList';
import { getSpamListNextPage } from '../../actions/spamList';
import { getUnreadListNextPage } from '../../actions/unreadList';
import { ChatList, ChatListLayout } from '../../components/ChatList';
import { ChatListSkeleton } from '../../components/ChatListSkeleton';
import { EmptyChatList } from '../../components/EmptyChatList';
import { useChatsToDisplay } from '../../hooks/useChatsToDisplay';
import { chatListSelector } from '../../selectors';
import { ERequestApiStatus } from '../../types/appState';
import { ERoute } from '../../types/route';
import { LeftSwipeVisibleProvider } from '../../utils/useLeftSwipeVisible';
import { ChatListItemContainer } from '../ChatListItemContainer';
import { TopMenuContainer } from '../TopMenuContainer';

export const SubAgentChatListContainer: React.FC = () => {
  const dispatch = useDispatch();

  const { currentRoute, allChatsLoaded, status, isFirstLoad } = useSelector(chatListSelector);

  const chatsToDisplay = useChatsToDisplay();

  const getNextPage = React.useCallback(() => {
    if (currentRoute === ERoute.Spam) {
      return dispatch(getSpamListNextPage());
    }

    if (currentRoute === ERoute.Messages) {
      return dispatch(getChatListNextPage());
    }

    if (currentRoute === ERoute.Archive) {
      return dispatch(getArchivedListNextPage());
    }

    if (currentRoute === ERoute.Unread) {
      return dispatch(getUnreadListNextPage());
    }

    return;
  }, [dispatch, currentRoute]);

  const onScrollToEnd = React.useCallback(() => {
    if (allChatsLoaded || status === ERequestApiStatus.Pending) {
      return;
    }

    getNextPage();
  }, [allChatsLoaded, status, getNextPage]);

  React.useEffect(() => {
    if (isFirstLoad) {
      getNextPage();
    }
  }, [isFirstLoad, getNextPage]);

  const chatListContent = React.useMemo(() => {
    if (status === ERequestApiStatus.Pending && !chatsToDisplay.length) {
      return <ChatListSkeleton />;
    }

    return chatsToDisplay.length ? (
      <ChatList status={status} onScrollToEnd={onScrollToEnd}>
        <LeftSwipeVisibleProvider>
          {chatsToDisplay.map(chat => (
            <ChatListItemContainer chat={chat} key={`chat-item-${chat.chatId}`} />
          ))}
        </LeftSwipeVisibleProvider>
      </ChatList>
    ) : (
      <EmptyChatList route={currentRoute} withLinkButton={false} />
    );
  }, [status, chatsToDisplay, onScrollToEnd, currentRoute]);

  const props = React.useMemo(
    () => ({
      topMenu: <TopMenuContainer isChangeListVisible={false} />,
      chatListContent,
    }),
    [chatListContent],
  );

  return <ChatListLayout {...props} />;
};
